/**
 * # 모듈 상태 «국면» 집합 — 모니터링이 상태 이름을 보는 유일한 자리
 *
 * 🔴 **모니터링 코드는 트레이·수거 국면을 이름으로 직접 비교하지 않는다.** 여기 집합·함수로만 본다.
 *    종전에는 `stepActivityModel.ts` · `temporaryStepMock.ts` 가 `WAITING_FOR_UNLOAD` ·
 *    `WAITING_FOR_MANUAL_LOAD` 를 제각각 나열했고, 어휘가 24 → 30값이 되자(2026-09-26 · module-core
 *    `runtime.py` `_WAITING_FOR_UNLOAD = "WAITING_FOR_TRAY_UNLOAD"`) 새 이름이 오는 순간 트레이 국면을 놓쳤다.
 *
 * 🔴 **옛 이름과 새 이름을 같이 넣는다.** 근거: 정본 `06 §4.1` 「은퇴 상태 대응」 —
 *
 * | 은퇴 이름 | 새 이름 |
 * | --- | --- |
 * | `WAITING_FOR_UNLOAD` | `WAITING_FOR_TRAY_UNLOAD` |
 * | `WAITING_FOR_MANUAL_UNLOAD` | `WAITING_FOR_MANUAL_TRAY_UNLOAD` |
 * | `WAITING_FOR_MANUAL_LOAD` | `WAITING_FOR_MANUAL_TRAY_LOAD` |
 * | `OUTPUT` | 없음 |
 * | `LOADING`(염색기) | 없음 |
 *
 * 은퇴 이름은 계약 enum 과 되돌릴 수 없는 `event_log` 에 남는다(같은 절) — 실물 `GET /history` 에
 * `WAITING_FOR_UNLOAD` 가 아직 있다(2026-09-26 확인). 옛 기록을 그릴 때도 같은 국면이 나와야 한다.
 *
 * ⚠ 여기는 **국면(무엇이 어느 방향으로 움직이나)** 을 판정한다. **「누가 할 일인가」는 여기서 보지 않는다** —
 *   그건 `atoms/statusKind.ts`(작업 중 ↔ 개입 대기 · `06 §4.2a`)가 정한다.
 */

/** 사람 적재 대기 — 사람이 입구 트레이를 채우기를 기다린다(원심 `SM-MOD-06`) */
export const MANUAL_LOAD_WAIT_STATES: ReadonlySet<string> = new Set([
  'WAITING_FOR_MANUAL_TRAY_LOAD',
  'WAITING_FOR_MANUAL_LOAD', // 은퇴 → WAITING_FOR_MANUAL_TRAY_LOAD
])

/** 로봇 회수 대기 — 6축이 후면에서 핸드오프 트레이를 가져가기를 기다린다(원심·어셈블러) */
export const ROBOT_UNLOAD_WAIT_STATES: ReadonlySet<string> = new Set([
  'WAITING_FOR_TRAY_UNLOAD',
  'WAITING_FOR_UNLOAD', // 은퇴 → WAITING_FOR_TRAY_UNLOAD
])

/** 사람 수거 대기 — 사람이 전면에서 트레이를 가져가기를 기다린다(세 모듈) */
export const MANUAL_UNLOAD_WAIT_STATES: ReadonlySet<string> = new Set([
  'WAITING_FOR_MANUAL_TRAY_UNLOAD',
  'WAITING_FOR_MANUAL_UNLOAD', // 은퇴 → WAITING_FOR_MANUAL_TRAY_UNLOAD
])

/**
 * **수거 대기(로봇 · 사람)** — 공정이 끝나 산출 트레이가 나가기를 기다리는 국면.
 *
 * 🔴 **로봇 회수 대기와 사람 수거 대기를 한 국면으로 접는다.** 이 집합을 쓰는 판정은 셋뿐이고
 *    셋 다 «방향·자리»만 묻는다:
 *    ① `STATE_COMPONENT` — 어느 부품을 강조하나 → 둘 다 **출구 트레이**다
 *    ② `temporaryStepArgs` — 집기·놓기의 방향 → 둘 다 **반출 쪽**(로터·스테이지 → 출구)
 *    ③ `temporaryChamberFlow` — 칸의 소재 → 둘 다 **전부 완료 트레이**
 *    둘을 가르는 것은 「누가 가져가나」 하나이고 그건 `statusKind()` 가 따로 말한다(로봇 = 작업 중 ·
 *    사람 = 개입 대기). 여기서 갈라 두면 같은 트레이가 로봇을 기다릴 때와 사람을 기다릴 때
 *    그림이 달라지는데, 물리적으로 트레이는 같은 자리에 있다(`06 §4.2b` 원심·조립
 *    `WAITING_FOR_TRAY_UNLOAD → WAITING_FOR_MANUAL_TRAY_UNLOAD` — 「로봇 회수 확약 ∧ 전면 이동 완료」).
 * ⚠ 염색기에는 로봇 회수 대기가 없다(`COMPLETE → WAITING_FOR_MANUAL_TRAY_UNLOAD`). 그래도 같은 집합을 쓴다 —
 *   염색기의 옛 기록 `WAITING_FOR_UNLOAD` 가 대응표상 `WAITING_FOR_TRAY_UNLOAD` 로 가므로, 집합을 모듈별로
 *   쪼개면 염색기 옛 기록이 국면을 놓친다.
 */
export const UNLOAD_WAIT_STATES: ReadonlySet<string> = new Set([
  ...ROBOT_UNLOAD_WAIT_STATES,
  ...MANUAL_UNLOAD_WAIT_STATES,
])

/**
 * 트레이 이동 — 트레이가 모듈 경계를 넘는 동안(슬라이드 · 6축 놓기·집기 준비~확인 · `06 §4.2b` 트레이 행).
 * 2026-09-26 신설이라 옛 이름이 없다.
 */
export const TRAY_TRANSIT_STATES: ReadonlySet<string> = new Set(['TRAY_LOADING', 'TRAY_UNLOADING'])

/**
 * **반출 쪽 국면인가** — 챔버·트레이가 장비 안에서 출구로 나가는 방향.
 *
 * - `UNLOADING` — 원심 회차 회수 · 어셈블러 챔버 회수(2026-09-26 `SM-MOD-02` 재정리로 어셈블러도 갖는다)
 * - `OUTPUT` — 은퇴(어셈블러 옛 산출 · 대응 없음). 옛 기록용
 * - `TRAY_UNLOADING` — 트레이 반출
 * - 수거 대기 전체(`UNLOAD_WAIT_STATES`)
 *
 * 그 밖(`LOADING` · `TRAY_LOADING` · 적재 대기 · 작업 중 상태 · `undefined`)은 반입 쪽으로 본다.
 */
export function isOutboundState(state: string | undefined): boolean {
  if (state === undefined) return false
  return (
    state === 'UNLOADING' ||
    state === 'OUTPUT' ||
    state === 'TRAY_UNLOADING' ||
    UNLOAD_WAIT_STATES.has(state)
  )
}

/** 집합의 이름마다 같은 값을 매단 표 — 상태 → 부품 표를 집합으로 짓는 데 쓴다 */
export function forStates<V>(states: ReadonlySet<string>, value: V): Record<string, V> {
  return Object.fromEntries([...states].map((s) => [s, value]))
}
